/**
 * Dispatcher brain — STD-ordered board, clash detect, confirm-gated plan.
 *
 * Deterministic dispatch-core math on live desk data (missions, assignments,
 * fleet, crew, duty, Mx). No xAI. Propose never mutates the board; Accept does.
 */
import { MIN_TURN_MIN, assignmentForMission, openAssignments, repositionBetween } from "./assignment";
import { ferryTimesLegal, nextLegalInstant } from "./curfew";
import { assignablePilots } from "./crew";
import { dutyViewFor, halifaxDateKey, type DutyRoster, type DutyView } from "./duty";
import { applyAircraftToDraft } from "./generator";
import { CHARTER_RANGE_FACTOR } from "./hawker800xp";
import { canCarryMxPayload, destIsMroForType } from "./charterRealism";
import { isCharterAssignable } from "./maintenance";
import { aircraftMatchesRatings } from "./profile";
import { isCancelReason, stampCancel } from "./cancelReason";
import { collapseExistingSandwich, applyOvernightToMissions, type OvernightDecision } from "./ferryOvernight";
import { charterForFerry, ferryRole, shiftDependentFerries, tripUnit } from "./ferryInsert";
import type {
  AircraftSpec,
  CancelReasonCode,
  CharterRequest,
  CompanyMxBase,
  CrewMember,
  MelItem,
  Mission,
  SceneryAirport,
  Squawk,
} from "./types";
import type { TripAssignment } from "./assignment";

export const DISPATCH_PLAN_KIND = "northstar.dispatch.plan";

export type ClashKind =
  | "tail_overlap"
  | "crew_overlap"
  | "duty_overlap"
  | "impossible_turn"
  | "fdp_overflow"
  | "flight_time_overflow"
  | "type_constraint"
  | "grounded_tail"
  | "overnight_sandwich"
  | "weight";

export type ClashSeverity = "red" | "amber";

export type DispatchClash = {
  id: string;
  kind: ClashKind;
  severity: ClashSeverity;
  missionIds: string[];
  aircraftId?: string;
  crewId?: string;
  message: string;
  overnightIcao?: string;
  homeIcao?: string;
};

export type PlanActionKind = "reschedule" | "reassign_tail" | "reassign_crew" | "cancel_mission";

export type DispatchPlanAction = {
  id: string;
  kind: PlanActionKind;
  missionId: string;
  clashId: string;
  from: {
    departureIso?: string;
    aircraftId?: string;
    registration?: string;
    crewId?: string;
    crewName?: string;
  };
  to: {
    departureIso?: string;
    aircraftId?: string;
    registration?: string;
    crewId?: string;
    crewName?: string;
    overnightIcao?: string;
    homeIcao?: string;
  };
  summary: string;
  cancelMissionIds?: string[];
  cancelReason?: CancelReasonCode;
};

export type DispatchPlanStatus = "proposed" | "accepted" | "rejected";

export type DispatchPlan = {
  id: string;
  kind: typeof DISPATCH_PLAN_KIND;
  createdAtUtc: string;
  createdByCrewId: string;
  createdByName: string;
  status: DispatchPlanStatus;
  decidedAtUtc?: string;
  decidedByCrewId?: string;
  decidedByName?: string;
  boardDateKey: string;
  clashIds: string[];
  remainingClashIds: string[];
  actions: DispatchPlanAction[];
  summary: string;
  clashNotes: string[];
};

export type DispatchBoardRow = {
  mission: Mission;
  stdMs: number;
  etaMs: number;
  dateKey: string;
  crewId: string | null;
  crewName: string | null;
  clashIds: string[];
  clashKinds: ClashKind[];
};

export type PendingClashCall = {
  id: string;
  key: string;
  brief: string;
  crewId?: string;
  clashCount: number;
  createdAtUtc: string;
};

export type DispatchBrainInput = {
  missions: Mission[];
  assignments: TripAssignment[];
  fleet: AircraftSpec[];
  crew: CrewMember[];
  duty: DutyRoster;
  scenery: SceneryAirport[];
  inbox?: CharterRequest[];
  squawks?: Squawk[];
  melItems?: MelItem[];
  now?: Date;
  actorCrewId?: string;
  actorName?: string;
  homeIcao?: string;
  /** Company MX bases. Used only to recognize an already-filed mx-positioning dest. */
  mxBases?: readonly CompanyMxBase[];
};

export const CLASH_KIND_LABEL: Record<ClashKind, string> = {
  tail_overlap: "TAIL",
  crew_overlap: "CREW",
  duty_overlap: "DUTY",
  impossible_turn: "TURN",
  fdp_overflow: "FDP",
  flight_time_overflow: "FTIME",
  type_constraint: "TYPE",
  grounded_tail: "AOG",
  overnight_sandwich: "OVERNIGHT",
  weight: "WEIGHT",
};

/** Plain-language reason for Gantt conflict markers and tooltips. */
export const CLASH_KIND_REASON: Record<ClashKind, string> = {
  tail_overlap: "Aircraft double-booked",
  crew_overlap: "Crew duty overlap",
  duty_overlap: "Rest below minimum",
  impossible_turn: "Turn time too short",
  fdp_overflow: "Duty period over limit",
  flight_time_overflow: "Flight time over limit",
  type_constraint: "Aircraft or crew limit",
  grounded_tail: "Aircraft grounded",
  overnight_sandwich: "Overnight away from base",
  weight: "Weight or fuel over limit",
};

export function isOpenBoardMission(status: Mission["status"]): boolean {
  return status === "pending" || status === "in-progress";
}

export function missionStdMs(mission: Pick<Mission, "departureIso">): number {
  const t = new Date(mission.departureIso).getTime();
  return Number.isFinite(t) ? t : 0;
}

export function missionStaMs(mission: Pick<Mission, "departureIso" | "eteMin">): number {
  return missionStdMs(mission) + Math.max(25, mission.eteMin) * 60_000;
}

export function missionWindow(
  mission: Pick<Mission, "departureIso" | "eteMin">,
  padMin = MIN_TURN_MIN,
): { start: number; end: number } {
  return {
    start: missionStdMs(mission) - padMin * 60_000,
    end: missionStaMs(mission) + padMin * 60_000,
  };
}

export function windowsOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}

/**
 * Dest of the earlier sector is origin of the later, with at least company
 * turn between STA and next STD. That is a legal turn — not a double-book.
 * Inbound ferry + its charter use this (one ground turn, not two pads).
 */
export function isLegalConnectedTurn(
  a: Pick<Mission, "originIcao" | "destIcao" | "departureIso" | "eteMin">,
  b: Pick<Mission, "originIcao" | "destIcao" | "departureIso" | "eteMin">,
): boolean {
  const first = missionStdMs(a) <= missionStdMs(b) ? a : b;
  const second = first === a ? b : a;
  if (first.destIcao.toUpperCase() !== second.originIcao.toUpperCase()) return false;
  const gapMin = (missionStdMs(second) - missionStaMs(first)) / 60_000;
  return gapMin + 0.001 >= MIN_TURN_MIN;
}

export function compareMissionsByStd(a: Mission, b: Mission): number {
  const std = missionStdMs(a) - missionStdMs(b);
  if (std !== 0) return std;
  return a.id.localeCompare(b.id);
}

/** Pure STD / date order — the dispatcher day plan. */
export function sortMissionsByStd(missions: Mission[]): Mission[] {
  return [...missions].sort(compareMissionsByStd);
}

export function openBoardMissions(missions: Mission[]): Mission[] {
  return missions.filter((m) => isOpenBoardMission(m.status));
}

function clashId(kind: ClashKind, missionIds: string[]): string {
  return `${kind}:${[...missionIds].sort().join("+")}`;
}

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids)];
}

function mergePairClashes(rows: DispatchClash[]): DispatchClash[] {
  if (rows.length <= 1) return rows;
  const resource = (c: DispatchClash) => c.aircraftId || c.crewId || c.id;
  const buckets = new Map<string, DispatchClash[]>();
  for (const row of rows) {
    const key = `${row.kind}:${resource(row)}`;
    const list = buckets.get(key) ?? [];
    list.push(row);
    buckets.set(key, list);
  }
  return [...buckets.values()].map((list) => {
    if (list.length === 1) return list[0]!;
    const missionIds = uniqueIds(list.flatMap((c) => c.missionIds));
    const head = list[0]!;
    const who = head.kind === "tail_overlap" ? (head.message.split(" double-booked")[0] || "Tail") : head.message.split(" is on overlapping")[0] || "Crew";
    return {
      ...head,
      id: clashId(head.kind, missionIds),
      missionIds,
      message:
        head.kind === "tail_overlap"
          ? `${who} double-booked across ${missionIds.length} sectors — one tail, one day.`
          : `${who} is on overlapping FDPs across ${missionIds.length} sectors — one crew, one day.`,
    };
  });
}

/** Reciprocal CREW/TAIL pair lines collapse into one row per tail / crew. */
export function groupClashes(clashes: DispatchClash[]): DispatchClash[] {
  const pairs = clashes.filter((c) => c.kind === "tail_overlap" || c.kind === "crew_overlap");
  const rest = clashes.filter((c) => c.kind !== "tail_overlap" && c.kind !== "crew_overlap");
  return [...rest, ...mergePairClashes(pairs)];
}

export function clashNotifyKey(clashes: DispatchClash[]): string {
  return clashes
    .map((c) => c.id)
    .sort()
    .join("|");
}

export function clashNotifyWorse(prevKey: string, nextKey: string): boolean {
  if (!nextKey) return false;
  // Empty prev means the desk has not baselined this session yet.
  // Treating "" as "everything is new" rang Emerson on every open.
  if (!prevKey) return false;
  const seen = new Set(prevKey.split("|").filter(Boolean));
  return nextKey.split("|").some((id) => id && !seen.has(id));
}

export function composeClashCallBrief(clashes: DispatchClash[]): string {
  const overnight = clashes.find((c) => c.kind === "overnight_sandwich");
  const n = clashes.length;
  const lead = overnight
    ? `Company board is overbuilt. Prefer overnight at ${overnight.overnightIcao ?? "the away field"} instead of the empty home out-and-back.`
    : `Company board has ${n} schedule problem${n === 1 ? "" : "s"} — duty and overlap limits are real.`;
  const lines = clashes.slice(0, 4).map((c) => `${CLASH_KIND_LABEL[c.kind]}: ${c.message}`);
  return ["Celtic Air Dispatch.", lead, ...lines, "Open Dispatch assist and Propose plan. Accept is required. No inbox message — this is the call."].join(
    " ",
  );
}

function pushOvernightSandwich(input: DispatchBrainInput, push: (clash: DispatchClash) => void) {
  const home = input.homeIcao?.trim();
  if (!home) return;
  const seenAircraft = new Set<string>();
  for (const mission of input.missions) {
    if (!mission.aircraftId || seenAircraft.has(mission.aircraftId)) continue;
    seenAircraft.add(mission.aircraftId);
    const found = collapseExistingSandwich({
      missions: input.missions,
      inbox: input.inbox ?? [],
      homeIcao: home,
      aircraftId: mission.aircraftId,
    });
    if (!found.overnight || !found.cancelMissionIds.length) continue;
    const ids = uniqueIds([...found.cancelMissionIds, found.overnight.missionId]);
    push({
      id: clashId("overnight_sandwich", ids),
      kind: "overnight_sandwich",
      severity: "red",
      missionIds: ids,
      aircraftId: found.overnight.aircraftId,
      crewId: found.overnight.crewId,
      overnightIcao: found.overnight.icao,
      homeIcao: found.overnight.homeIcao,
      message: found.overnight.note,
    });
  }
}

function crewForMission(assignments: TripAssignment[], missionId: string): { crewId: string; crewName: string } | null {
  const row = assignmentForMission(assignments, missionId);
  if (!row?.crewId) return null;
  return { crewId: row.crewId, crewName: row.crewName };
}

function specFor(fleet: AircraftSpec[], aircraftId: string): AircraftSpec | undefined {
  return fleet.find((a) => a.id === aircraftId);
}

function mxPositioningOk(mission: Mission, spec: AircraftSpec, mxBases?: readonly CompanyMxBase[]): boolean {
  return (
    Boolean(mission.mxPositioning) &&
    canCarryMxPayload(spec) &&
    destIsMroForType(mission.destIcao, spec.icaoType, mxBases)
  );
}

function typeConstraintMessage(
  mission: Mission,
  spec: AircraftSpec,
  crewRated: string[] | undefined,
  squawks: Squawk[],
  melItems: MelItem[],
  mxBases?: readonly CompanyMxBase[],
): { kind: ClashKind; message: string } | null {
  const ferry = mission.flightType === "ferry" || mission.isMaintenance;
  if (!isCharterAssignable(spec, squawks, melItems) && !mxPositioningOk(mission, spec, mxBases)) {
    return {
      kind: "grounded_tail",
      message: `${mission.callsign} is on ${spec.registration} (${spec.status.replace("_", " ")}) — not assignable.`,
    };
  }
  if (crewRated && !aircraftMatchesRatings(spec, crewRated)) {
    return {
      kind: "type_constraint",
      message: `${mission.callsign}: assigned crew is not rated on ${spec.icaoType}.`,
    };
  }
  if (mission.paxCount > spec.maxPax) {
    return {
      kind: "type_constraint",
      message: `${mission.callsign} has ${mission.paxCount} pax; ${spec.registration} max is ${spec.maxPax}.`,
    };
  }
  const limit = ferry ? spec.ferryRangeNm : spec.rangeNm;
  const stageNm =
    mission.legs?.length && mission.legs.every((leg) => Number.isFinite(leg.distanceNm))
      ? Math.max(...mission.legs.map((leg) => leg.distanceNm))
      : mission.distanceNm;
  if (stageNm > limit * CHARTER_RANGE_FACTOR) {
    return {
      kind: "type_constraint",
      message: `${mission.callsign} ${stageNm} NM exceeds ${spec.registration} ${ferry ? "ferry " : ""}range ${limit} NM.`,
    };
  }
  return null;
}

function requiredTurnMin(
  prev: Mission,
  next: Mission,
  spec: AircraftSpec | undefined,
  scenery: SceneryAirport[],
): number {
  if (prev.destIcao.toUpperCase() === next.originIcao.toUpperCase()) return MIN_TURN_MIN;
  if (!spec) return MIN_TURN_MIN + 45;
  return repositionBetween(prev.destIcao, next.originIcao, spec, scenery).eteMin + MIN_TURN_MIN;
}

function dutyForCrew(duty: DutyRoster, crewId: string, crewName: string, now: Date): DutyView {
  return dutyViewFor(duty, { id: crewId, name: crewName }, now);
}

function ratedTypesFor(crew: CrewMember[], crewId: string | null): string[] | undefined {
  if (!crewId) return undefined;
  const member = crew.find((c) => c.id === crewId);
  return member?.ratedIcaoTypes.length ? member.ratedIcaoTypes : undefined;
}

/** Clash math — live board / fleet / duty / Mx only. */
export function detectClashes(input: DispatchBrainInput): DispatchClash[] {
  const now = input.now ?? new Date();
  const squawks = input.squawks ?? [];
  const melItems = input.melItems ?? [];
  const open = sortMissionsByStd(openBoardMissions(input.missions));
  const clashes: DispatchClash[] = [];
  const seen = new Set<string>();

  const push = (clash: DispatchClash) => {
    if (seen.has(clash.id)) return;
    seen.add(clash.id);
    clashes.push(clash);
  };

  for (const mission of open) {
    const spec = specFor(input.fleet, mission.aircraftId);
    const assigned = crewForMission(input.assignments, mission.id);
    const rated = ratedTypesFor(input.crew, assigned?.crewId ?? null);
    if (!spec) {
      push({
        id: clashId("type_constraint", [mission.id]),
        kind: "type_constraint",
        severity: "red",
        missionIds: [mission.id],
        aircraftId: mission.aircraftId,
        message: `${mission.callsign} tail ${mission.registration} is not on the company fleet.`,
      });
      continue;
    }
    const typed = typeConstraintMessage(mission, spec, assigned ? rated : undefined, squawks, melItems, input.mxBases);
    if (typed) {
      push({
        id: clashId(typed.kind, [mission.id]),
        kind: typed.kind,
        severity: "red",
        missionIds: [mission.id],
        aircraftId: spec.id,
        crewId: assigned?.crewId,
        message: typed.message,
      });
    }
  }

  for (let i = 0; i < open.length; i++) {
    const a = open[i]!;
    for (let j = i + 1; j < open.length; j++) {
      const b = open[j]!;
      const wa = missionWindow(a);
      const wb = missionWindow(b);
      if (!windowsOverlap(wa.start, wa.end, wb.start, wb.end)) continue;
      if (isLegalConnectedTurn(a, b)) continue;
      if (a.aircraftId && a.aircraftId === b.aircraftId) {
        push({
          id: clashId("tail_overlap", [a.id, b.id]),
          kind: "tail_overlap",
          severity: "red",
          missionIds: [a.id, b.id],
          aircraftId: a.aircraftId,
          message: `${a.registration} double-booked: ${a.callsign} ${a.originIcao}–${a.destIcao} overlaps ${b.callsign} ${b.originIcao}–${b.destIcao}.`,
        });
      }
      const crewA = crewForMission(input.assignments, a.id);
      const crewB = crewForMission(input.assignments, b.id);
      if (crewA && crewB && crewA.crewId === crewB.crewId) {
        push({
          id: clashId("crew_overlap", [a.id, b.id]),
          kind: "crew_overlap",
          severity: "red",
          missionIds: [a.id, b.id],
          crewId: crewA.crewId,
          message: `${crewA.crewName} is on overlapping FDPs: ${a.callsign} and ${b.callsign}.`,
        });
      }
    }
  }

  const byTail = new Map<string, Mission[]>();
  for (const mission of open) {
    if (!mission.aircraftId) continue;
    const list = byTail.get(mission.aircraftId) ?? [];
    list.push(mission);
    byTail.set(mission.aircraftId, list);
  }
  for (const [aircraftId, list] of byTail) {
    const ordered = sortMissionsByStd(list);
    const spec = specFor(input.fleet, aircraftId);
    for (let i = 0; i < ordered.length - 1; i++) {
      const prev = ordered[i]!;
      const next = ordered[i + 1]!;
      const wa = missionWindow(prev);
      const wb = missionWindow(next);
      if (windowsOverlap(wa.start, wa.end, wb.start, wb.end)) continue;
      const need = requiredTurnMin(prev, next, spec, input.scenery);
      const gapMin = (missionStdMs(next) - missionStaMs(prev)) / 60_000;
      if (gapMin + 0.001 < need) {
        const sameField = prev.destIcao.toUpperCase() === next.originIcao.toUpperCase();
        push({
          id: clashId("impossible_turn", [prev.id, next.id]),
          kind: "impossible_turn",
          severity: "red",
          missionIds: [prev.id, next.id],
          aircraftId,
          message: sameField
            ? `${prev.registration} turn ${prev.callsign}→${next.callsign} at ${next.originIcao} is ${Math.max(0, Math.round(gapMin))} min (need ${need} incl. ground).`
            : `${prev.registration} cannot make ${prev.destIcao}→${next.originIcao} after ${prev.callsign} before ${next.callsign} STD (gap ${Math.max(0, Math.round(gapMin))} min, need ${need} incl. ETE + turn).`,
        });
      }
    }
  }

  // FDP and flight time are per duty day. Bucket by the Halifax date of STD so
  // a Monday trip plus a Friday trip is not reported as one impossible day.
  const byCrew = new Map<string, { crewId: string; name: string; missions: Mission[] }>();
  for (const mission of open) {
    const crew = crewForMission(input.assignments, mission.id);
    if (!crew) continue;
    const key = `${crew.crewId}|${halifaxDateKey(new Date(mission.departureIso))}`;
    const row = byCrew.get(key) ?? { crewId: crew.crewId, name: crew.crewName, missions: [] };
    row.missions.push(mission);
    byCrew.set(key, row);
  }
  for (const row of byCrew.values()) {
    const crewId = row.crewId;
    const ordered = sortMissionsByStd(row.missions);
    const duty = dutyForCrew(input.duty, crewId, row.name, now);
    const blockMin = ordered.reduce((sum, m) => sum + Math.max(25, m.eteMin), 0) + Math.max(0, ordered.length - 1) * MIN_TURN_MIN;
    const first = ordered[0];
    const last = ordered[ordered.length - 1];
    const spanMin =
      first && last ? Math.max(0, Math.round((missionStaMs(last) - missionStdMs(first)) / 60_000)) : blockMin;
    // Today's remaining FDP only binds legs the crew cannot reach after closing
    // this duty and taking a full rest; a later bucket is a fresh duty day.
    const freshDayFromMs = now.getTime() + (Math.max(0, duty.fdpRemainMin) + duty.limits.minRestMin) * 60_000;
    const inCurrentDuty = duty.status === "on" && Boolean(first) && missionStdMs(first!) < freshDayFromMs;
    const flightCap = inCurrentDuty ? duty.flightRemainMin : duty.limits.maxFlightTimeMin;
    const fdpCap = inCurrentDuty ? duty.fdpRemainMin : duty.limits.maxFdpMin;
    if (blockMin > flightCap) {
      push({
        id: clashId("flight_time_overflow", ordered.map((m) => m.id)),
        kind: "flight_time_overflow",
        severity: "red",
        missionIds: ordered.map((m) => m.id),
        crewId,
        message: `${row.name} stacked block ${blockMin} min exceeds remaining / max flight ${Math.max(0, Math.round(flightCap))} (${duty.certificateLabel}).`,
      });
    }
    if (spanMin > fdpCap || blockMin > fdpCap) {
      push({
        id: clashId("fdp_overflow", ordered.map((m) => m.id)),
        kind: "fdp_overflow",
        severity: "red",
        missionIds: ordered.map((m) => m.id),
        crewId,
        message: `${row.name} day span ${spanMin} min / block ${blockMin} min exceeds remaining / max FDP ${Math.max(0, Math.round(fdpCap))} (${duty.certificateLabel}).`,
      });
    }
  }

  for (const mission of open) {
    const assigned = crewForMission(input.assignments, mission.id);
    if (!assigned) continue;
    const duty = dutyForCrew(input.duty, assigned.crewId, assigned.crewName, now);
    const session = duty.session;
    if (duty.status !== "rest" || !session?.restStartedAtUtc) continue;
    const restStart = new Date(session.restStartedAtUtc).getTime();
    if (!Number.isFinite(restStart)) continue;
    const restEnd = session.restEndedAtUtc
      ? new Date(session.restEndedAtUtc).getTime()
      : now.getTime() + Math.max(duty.restRemainMin ?? 0, duty.limits.minRestMin) * 60_000;
    if (!Number.isFinite(restEnd)) continue;
    if (!windowsOverlap(missionStdMs(mission), missionStaMs(mission), restStart, restEnd)) continue;
    push({
      id: clashId("duty_overlap", [mission.id]),
      kind: "duty_overlap",
      severity: "red",
      missionIds: [mission.id],
      crewId: assigned.crewId,
      message: `${assigned.crewName} rest window overlaps ${mission.callsign} ${mission.originIcao}–${mission.destIcao} (${duty.certificateLabel}).`,
    });
  }

  pushOvernightSandwich(input, push);

  const grouped = groupClashes(clashes);
  return grouped.sort((a, b) => {
    const rank = (kind: ClashKind) => (kind === "overnight_sandwich" ? 0 : kind === "fdp_overflow" || kind === "flight_time_overflow" || kind === "duty_overlap" ? 2 : 1);
    const rankDiff = rank(a.kind) - rank(b.kind);
    if (rankDiff !== 0) return rankDiff;
    const aStd = Math.min(...a.missionIds.map((id) => missionStdMs(open.find((m) => m.id === id) ?? { departureIso: "" })));
    const bStd = Math.min(...b.missionIds.map((id) => missionStdMs(open.find((m) => m.id === id) ?? { departureIso: "" })));
    if (aStd !== bStd) return aStd - bStd;
    return a.id.localeCompare(b.id);
  });
}

export function buildDispatchBoard(
  missions: Mission[],
  assignments: TripAssignment[],
  clashes: DispatchClash[],
): DispatchBoardRow[] {
  const byMission = new Map<string, DispatchClash[]>();
  for (const clash of clashes) {
    for (const id of clash.missionIds) {
      const list = byMission.get(id) ?? [];
      list.push(clash);
      byMission.set(id, list);
    }
  }
  return sortMissionsByStd(missions).map((mission) => {
    const hits = byMission.get(mission.id) ?? [];
    const crew = crewForMission(assignments, mission.id);
    return {
      mission,
      stdMs: missionStdMs(mission),
      etaMs: missionStaMs(mission),
      dateKey: halifaxDateKey(new Date(mission.departureIso)),
      crewId: crew?.crewId ?? null,
      crewName: crew?.crewName ?? null,
      clashIds: hits.map((c) => c.id),
      clashKinds: [...new Set(hits.map((c) => c.kind))],
    };
  });
}

export function clashesForMission(clashes: DispatchClash[], missionId: string): DispatchClash[] {
  return clashes.filter((c) => c.missionIds.includes(missionId));
}

export function formatDispatchAssistBrief(clashes: DispatchClash[], plan: DispatchPlan | null): string {
  if (!clashes.length && (!plan || plan.status !== "proposed")) {
    return "Board is clean — no clashes on live desk data.";
  }
  if (plan?.status === "proposed") return plan.summary;
  if (plan?.status === "accepted") return `Accepted: ${plan.summary}`;
  if (plan?.status === "rejected") return `Rejected: ${plan.summary}`;
  const overnight = clashes.find((c) => c.kind === "overnight_sandwich");
  const n = clashes.filter((c) => c.severity === "red").length;
  if (overnight) {
    return `Stay overnight at ${overnight.overnightIcao ?? "the away field"} instead of the empty home out-and-back. ${n} schedule problem${n === 1 ? "" : "s"} — Propose a fix (Accept required).`;
  }
  return `${n} schedule problem${n === 1 ? "" : "s"} on the company board. Duty / overlap limits are real — Propose a fix (Accept required).`;
}

function laterMission(missions: Mission[], ids: string[]): Mission | undefined {
  const rows = ids
    .map((id) => missions.find((m) => m.id === id))
    .filter((m): m is Mission => Boolean(m));
  return sortMissionsByStd(rows).at(-1);
}

function earlierMission(missions: Mission[], ids: string[]): Mission | undefined {
  const rows = ids
    .map((id) => missions.find((m) => m.id === id))
    .filter((m): m is Mission => Boolean(m));
  return sortMissionsByStd(rows)[0];
}

function tailFitsMission(
  mission: Mission,
  spec: AircraftSpec,
  working: Mission[],
  assignments: TripAssignment[],
  scenery: SceneryAirport[],
  squawks: Squawk[],
  melItems: MelItem[],
  crewRated: string[] | undefined,
  mxBases?: readonly CompanyMxBase[],
): boolean {
  if (typeConstraintMessage(mission, spec, crewRated, squawks, melItems, mxBases)) return false;
  const tripStart = missionStdMs(mission);
  const tripEnd = missionStaMs(mission);
  return !working.some((m) => {
    if (m.id === mission.id || m.aircraftId !== spec.id) return false;
    if (!isOpenBoardMission(m.status)) return false;
    const w = missionWindow(m);
    return windowsOverlap(w.start, w.end, tripStart - MIN_TURN_MIN * 60_000, tripEnd + MIN_TURN_MIN * 60_000);
  });
}

function crewFitsMission(
  mission: Mission,
  crewId: string,
  working: Mission[],
  assignments: TripAssignment[],
): boolean {
  const tripStart = missionStdMs(mission);
  const tripEnd = missionStaMs(mission);
  return !openAssignments(assignments).some((a) => {
    if (a.crewId !== crewId || a.missionId === mission.id) return false;
    const other = a.missionId ? working.find((m) => m.id === a.missionId) : undefined;
    if (!other || !isOpenBoardMission(other.status)) return false;
    const w = missionWindow(other);
    return windowsOverlap(w.start, w.end, tripStart - MIN_TURN_MIN * 60_000, tripEnd + MIN_TURN_MIN * 60_000);
  });
}

function patchMission(list: Mission[], next: Mission): Mission[] {
  return list.map((m) => (m.id === next.id ? next : m));
}

function patchAssignmentAircraft(list: TripAssignment[], missionId: string, spec: AircraftSpec): TripAssignment[] {
  return list.map((a) =>
    a.missionId === missionId && !a.superseded
      ? { ...a, aircraftId: spec.id, registration: spec.registration }
      : a,
  );
}

function patchAssignmentCrew(
  list: TripAssignment[],
  missionId: string,
  crewId: string,
  crewName: string,
): TripAssignment[] {
  return list.map((a) => (a.missionId === missionId && !a.superseded ? { ...a, crewId, crewName } : a));
}

function rescheduleMission(mission: Mission, departureIso: string): Mission {
  const legs = mission.legs?.length
    ? mission.legs.map((leg, i) => (i === 0 ? { ...leg, departureIso } : leg))
    : mission.legs;
  return { ...mission, departureIso, legs };
}

const RELIEF_STEP_MS = 15 * 60_000;
const RELIEF_HORIZON_MS = 48 * 60 * 60_000;

function clashCount(input: DispatchBrainInput, missions: Mission[], assignments: TripAssignment[], now: Date): number {
  return detectClashes({ ...input, now, missions, assignments }).length;
}

function placedUnitClearsCurfew(missions: Mission[], unitIds: ReadonlySet<string>, charterId: string): boolean {
  for (const row of missions) {
    const inUnit = unitIds.has(row.id) || row.id === charterId || row.parentMissionId === charterId;
    if (!inUnit || !isOpenBoardMission(row.status)) continue;
    if (!ferryTimesLegal(row.originIcao, row.destIcao, new Date(row.departureIso), row.eteMin).legal) return false;
  }
  return true;
}

/**
 * Move one whole trip (charter + its ferries) to the earliest STD that sits
 * after minimum rest, inside curfew, and off every other leg on the tail and
 * the crew. Nothing is offered when that search cannot strictly cut the clash
 * count inside 48 h.
 */
function proposeDutyRelief(
  clash: DispatchClash,
  workingMissions: Mission[],
  workingAssignments: TripAssignment[],
  input: DispatchBrainInput,
): DispatchPlanAction | null {
  const now = input.now ?? new Date();
  const beforeCount = clashCount(input, workingMissions, workingAssignments, now);
  const rows = clash.missionIds
    .map((id) => workingMissions.find((m) => m.id === id))
    .filter((m): m is Mission => Boolean(m))
    .sort((a, b) => missionStdMs(a) - missionStdMs(b));
  const seen = new Set<string>();
  const units: Mission[][] = [];
  for (const row of rows) {
    const unit = tripUnit(workingMissions, row.id, workingAssignments).filter((m) => isOpenBoardMission(m.status));
    const key = unit.map((m) => m.id).sort().join("+");
    if (!unit.length || seen.has(key)) continue;
    seen.add(key);
    units.push(unit);
  }
  if (!units.length) return null;
  const horizon = now.getTime() + RELIEF_HORIZON_MS;

  for (const unit of [...units].reverse()) {
    const charter = unit.find((m) => m.flightType !== "ferry" && !m.isMaintenance) ?? unit[0]!;
    const movingIds = new Set(unit.map((m) => m.id));
    const kept = rows.filter((m) => !movingIds.has(m.id));
    const assigned =
      crewForMission(workingAssignments, charter.id) ?? crewForMission(workingAssignments, unit[0]!.id);
    const duty = assigned ? dutyForCrew(input.duty, assigned.crewId, assigned.crewName, now) : undefined;
    const minRestMin = duty?.limits.minRestMin ?? 10 * 60;
    const keptEnd = kept.length ? Math.max(...kept.map((m) => missionStaMs(m))) : now.getTime();
    let restEnd = keptEnd + minRestMin * 60_000;
    const session = duty?.session;
    if (duty?.status === "rest" && session?.restStartedAtUtc) {
      const restStop = session.restEndedAtUtc
        ? new Date(session.restEndedAtUtc).getTime()
        : now.getTime() + Math.max(duty.restRemainMin ?? 0, minRestMin) * 60_000;
      if (Number.isFinite(restStop)) restEnd = Math.max(restEnd, restStop);
    }
    if (restEnd > horizon) continue;
    const unitIds = new Set(unit.map((m) => m.id));
    for (let t = Math.max(restEnd, now.getTime()); t <= horizon; ) {
      const legal = nextLegalInstant(charter.originIcao, new Date(t));
      const legalMs = legal.getTime();
      if (!Number.isFinite(legalMs) || legalMs > horizon) break;
      const iso = legal.toISOString();
      t = legalMs + RELIEF_STEP_MS;
      if (iso === charter.departureIso) continue;
      const action: DispatchPlanAction = {
        id: newActionId("reschedule", charter.id, clash.id),
        kind: "reschedule",
        missionId: charter.id,
        clashId: clash.id,
        from: { departureIso: charter.departureIso },
        to: { departureIso: iso },
        summary: `Slip ${charter.callsign}${unit.length > 1 ? " and its ferry" : ""} to ${iso} after rest, inside curfew, clear of the tail and crew.`,
      };
      const applied = applyActionToWorking(action, workingMissions, workingAssignments, input.fleet, input.scenery);
      if (!placedUnitClearsCurfew(applied.missions, unitIds, charter.id)) continue;
      if (clashCount(input, applied.missions, applied.assignments, now) < beforeCount) return action;
    }
  }
  return null;
}

function pickSpareTail(
  mission: Mission,
  working: Mission[],
  assignments: TripAssignment[],
  fleet: AircraftSpec[],
  scenery: SceneryAirport[],
  squawks: Squawk[],
  melItems: MelItem[],
  crewRated: string[] | undefined,
  mxBases?: readonly CompanyMxBase[],
): AircraftSpec | null {
  const ranked = [...fleet].sort((a, b) => {
    const sameA = a.icaoType === (specFor(fleet, mission.aircraftId)?.icaoType ?? "") ? 0 : 1;
    const sameB = b.icaoType === (specFor(fleet, mission.aircraftId)?.icaoType ?? "") ? 0 : 1;
    if (sameA !== sameB) return sameA - sameB;
    return a.registration.localeCompare(b.registration);
  });
  for (const spec of ranked) {
    if (spec.id === mission.aircraftId) continue;
    if (tailFitsMission(mission, spec, working, assignments, scenery, squawks, melItems, crewRated, mxBases)) {
      return spec;
    }
  }
  return null;
}

function pickSpareCrew(
  mission: Mission,
  working: Mission[],
  assignments: TripAssignment[],
  crew: CrewMember[],
  fleet: AircraftSpec[],
  currentCrewId: string | undefined,
): CrewMember | null {
  const spec = specFor(fleet, mission.aircraftId);
  const pilots = assignablePilots(crew).slice().sort((a, b) => a.displayName.localeCompare(b.displayName));
  for (const pilot of pilots) {
    if (pilot.id === currentCrewId) continue;
    const rated = pilot.ratedIcaoTypes.length ? pilot.ratedIcaoTypes : undefined;
    if (spec && rated && !aircraftMatchesRatings(spec, rated)) continue;
    if (crewFitsMission(mission, pilot.id, working, assignments)) return pilot;
  }
  return null;
}

function newActionId(kind: PlanActionKind, missionId: string, clashId: string): string {
  return `${kind}:${missionId}:${clashId}`;
}

function applyActionToWorking(
  action: DispatchPlanAction,
  workingMissions: Mission[],
  workingAssignments: TripAssignment[],
  fleet: AircraftSpec[],
  scenery: SceneryAirport[],
): { missions: Mission[]; assignments: TripAssignment[] } {
  if (action.kind === "cancel_mission") {
    const ids = new Set([action.missionId, ...(action.cancelMissionIds ?? [])]);
    const reason = action.cancelReason ?? "ferry_sandwich";
    let missions = workingMissions.map((m) =>
      ids.has(m.id)
        ? stampCancel(m, {
            cancelReason: reason,
            cancelReasonNote: action.summary,
          })
        : m,
    );
    const overnightIcao = action.to.overnightIcao;
    const homeIcao = action.to.homeIcao;
    if (overnightIcao && homeIcao) {
      const cancelled = workingMissions.filter((m) => ids.has(m.id));
      const parent =
        missions.find((m) => m.id === action.missionId && m.status !== "cancelled") ??
        missions.find((m) => !ids.has(m.id) && m.originIcao.toUpperCase() === overnightIcao.toUpperCase() && m.status !== "cancelled");
      const decision: OvernightDecision = {
        icao: overnightIcao,
        homeIcao,
        aircraftId: cancelled[0]?.aircraftId ?? parent?.aircraftId ?? "",
        missionId: parent?.id ?? action.missionId,
        cancelledFerryIds: [...ids],
        cancelledRoutes: cancelled.map((m) => `${m.originIcao}–${m.destIcao}`),
        nextOriginIcao: parent?.originIcao ?? overnightIcao,
        nextDestIcao: parent?.destIcao ?? overnightIcao,
        note: action.summary,
        brief: action.summary,
        key: `plan:${overnightIcao}:${action.missionId}`,
      };
      missions = applyOvernightToMissions(missions, decision);
    }
    return { missions, assignments: workingAssignments };
  }
  const mission = workingMissions.find((m) => m.id === action.missionId);
  if (!mission) return { missions: workingMissions, assignments: workingAssignments };
  if (action.kind === "reschedule" && action.to.departureIso) {
    const next = rescheduleMission(mission, action.to.departureIso);
    let missions = patchMission(workingMissions, next);
    const role = mission.flightType === "ferry" ? ferryRole(workingMissions, mission, workingAssignments) : null;
    if (role === "positioning") {
      const parent =
        charterForFerry(missions, mission, workingAssignments) ??
        missions.find((m) => m.id === mission.parentMissionId && isOpenBoardMission(m.status)) ??
        missions.find(
          (m) =>
            Boolean(mission.inboxRequestId) &&
            m.inboxRequestId === mission.inboxRequestId &&
            m.flightType !== "ferry" &&
            isOpenBoardMission(m.status),
        );
      if (parent) {
        const linkedStd =
          new Date(action.to.departureIso).getTime() +
          Math.max(25, mission.eteMin) * 60_000 +
          MIN_TURN_MIN * 60_000;
        if (linkedStd > missionStdMs(parent) + 60_000) {
          const slipped = rescheduleMission(parent, new Date(linkedStd).toISOString());
          missions = shiftDependentFerries(patchMission(missions, slipped), slipped);
        }
      }
    } else if (role === "return") {
      const slippedAt = new Date(action.to.departureIso).getTime();
      missions = shiftDependentFerries(missions, next, new Date(slippedAt), { afterMs: slippedAt });
    } else if (mission.flightType !== "ferry") {
      missions = shiftDependentFerries(missions, next);
    }
    return { missions, assignments: workingAssignments };
  }
  if (action.kind === "reassign_tail" && action.to.aircraftId) {
    const spec = specFor(fleet, action.to.aircraftId);
    if (!spec) return { missions: workingMissions, assignments: workingAssignments };
    // Re-tail recomputes ETE / fuel / legs, but the company flight number is the
    // canonical ticket id — it must survive a tail swap.
    const next: Mission = {
      ...(applyAircraftToDraft(mission, spec, scenery) as Mission),
      callsign: mission.callsign,
      flightNumber: mission.flightNumber,
    };
    return {
      missions: patchMission(workingMissions, next),
      assignments: patchAssignmentAircraft(workingAssignments, mission.id, spec),
    };
  }
  if (action.kind === "reassign_crew" && action.to.crewId) {
    return {
      missions: workingMissions,
      assignments: patchAssignmentCrew(
        workingAssignments,
        mission.id,
        action.to.crewId,
        action.to.crewName ?? action.to.crewId,
      ),
    };
  }
  return { missions: workingMissions, assignments: workingAssignments };
}

/** Apply a confirm-accepted plan. Pure — store writes the result. */
export function applyDispatchPlan(
  input: Pick<DispatchBrainInput, "missions" | "assignments" | "fleet" | "scenery">,
  plan: DispatchPlan,
): { missions: Mission[]; assignments: TripAssignment[] } {
  let missions = input.missions.map((m) => ({ ...m, legs: m.legs ? m.legs.map((l) => ({ ...l })) : m.legs }));
  let assignments = input.assignments.map((a) => ({ ...a }));
  for (const action of plan.actions) {
    const next = applyActionToWorking(action, missions, assignments, input.fleet, input.scenery);
    missions = next.missions;
    assignments = next.assignments;
  }
  return { missions, assignments };
}

function proposeActionForClash(
  clash: DispatchClash,
  workingMissions: Mission[],
  workingAssignments: TripAssignment[],
  input: DispatchBrainInput,
): DispatchPlanAction | null {
  if (clash.kind === "overnight_sandwich") {
    const ferryIds = clash.missionIds.filter((id) => {
      const m = workingMissions.find((row) => row.id === id);
      return m && (m.flightType === "ferry" || m.isMaintenance) && m.status !== "cancelled";
    });
    const first = ferryIds[0];
    if (!first) return null;
    const routes = ferryIds
      .map((id) => workingMissions.find((m) => m.id === id))
      .filter((m): m is Mission => Boolean(m))
      .map((m) => `${m.originIcao}–${m.destIcao}`);
    return {
      id: newActionId("cancel_mission", first, clash.id),
      kind: "cancel_mission",
      missionId: first,
      clashId: clash.id,
      from: {},
      to: { overnightIcao: clash.overnightIcao, homeIcao: clash.homeIcao ?? input.homeIcao },
      cancelMissionIds: ferryIds,
      cancelReason: "ferry_sandwich",
      summary: `Cancel empty ${routes.join(" and ") || "ferry pair"} — overnight at ${clash.overnightIcao ?? "the away field"} instead of the out-and-back. Reason: redundant ferry pair.`,
    };
  }
  const later = laterMission(workingMissions, clash.missionIds);
  const earlier = earlierMission(workingMissions, clash.missionIds);
  if (!later) return null;
  const assigned = crewForMission(workingAssignments, later.id);
  const rated = ratedTypesFor(input.crew, assigned?.crewId ?? null);
  const squawks = input.squawks ?? [];
  const melItems = input.melItems ?? [];

  if (clash.kind === "grounded_tail" || clash.kind === "type_constraint" || clash.kind === "tail_overlap") {
    const spare = pickSpareTail(
      later,
      workingMissions,
      workingAssignments,
      input.fleet,
      input.scenery,
      squawks,
      melItems,
      rated,
      input.mxBases,
    );
    if (spare) {
      return {
        id: newActionId("reassign_tail", later.id, clash.id),
        kind: "reassign_tail",
        missionId: later.id,
        clashId: clash.id,
        from: { aircraftId: later.aircraftId, registration: later.registration },
        to: { aircraftId: spare.id, registration: spare.registration },
        summary: `Reassign ${later.callsign} ${later.originIcao}–${later.destIcao} ${later.registration} → ${spare.registration}.`,
      };
    }
  }

  if (
    clash.kind === "crew_overlap" ||
    clash.kind === "duty_overlap" ||
    clash.kind === "fdp_overflow" ||
    clash.kind === "flight_time_overflow"
  ) {
    const spare = pickSpareCrew(
      later,
      workingMissions,
      workingAssignments,
      input.crew,
      input.fleet,
      assigned?.crewId,
    );
    if (spare && assigned) {
      return {
        id: newActionId("reassign_crew", later.id, clash.id),
        kind: "reassign_crew",
        missionId: later.id,
        clashId: clash.id,
        from: { crewId: assigned.crewId, crewName: assigned.crewName },
        to: { crewId: spare.id, crewName: spare.displayName },
        summary: `Reassign ${later.callsign} crew ${assigned.crewName} → ${spare.displayName}.`,
      };
    }
  }

  if (clash.kind === "impossible_turn" || clash.kind === "tail_overlap") {
    if (earlier && earlier.id !== later.id) {
      const spec = specFor(input.fleet, later.aircraftId) ?? specFor(input.fleet, earlier.aircraftId);
      const need = requiredTurnMin(earlier, later, spec, input.scenery);
      const iso = new Date(missionStaMs(earlier) + (need + MIN_TURN_MIN) * 60_000).toISOString();
      if (iso !== later.departureIso) {
        return {
          id: newActionId("reschedule", later.id, clash.id),
          kind: "reschedule",
          missionId: later.id,
          clashId: clash.id,
          from: { departureIso: later.departureIso },
          to: { departureIso: iso },
          summary: `Slip ${later.callsign} STD to ${iso} (after ${earlier.callsign} + turn).`,
        };
      }
    }
  }

  if (clash.kind === "fdp_overflow" || clash.kind === "flight_time_overflow" || clash.kind === "duty_overlap") {
    return proposeDutyRelief(clash, workingMissions, workingAssignments, input);
  }

  if (clash.kind === "crew_overlap" && earlier && earlier.id !== later.id) {
    const iso = new Date(missionStaMs(earlier) + 2 * MIN_TURN_MIN * 60_000).toISOString();
    if (iso !== later.departureIso) {
      return {
        id: newActionId("reschedule", later.id, clash.id),
        kind: "reschedule",
        missionId: later.id,
        clashId: clash.id,
        from: { departureIso: later.departureIso },
        to: { departureIso: iso },
        summary: `Slip ${later.callsign} STD to ${iso} so crew is free after ${earlier.callsign}.`,
      };
    }
  }

  return null;
}

/** Build a confirm-gated plan. Does not rewrite the day. */
export function proposeDispatchPlan(input: DispatchBrainInput): DispatchPlan | null {
  const now = input.now ?? new Date();
  const clashes = detectClashes(input);
  if (!clashes.length) return null;

  let workingMissions = input.missions.map((m) => ({ ...m, legs: m.legs ? m.legs.map((l) => ({ ...l })) : m.legs }));
  let workingAssignments = input.assignments.map((a) => ({ ...a }));
  const actions: DispatchPlanAction[] = [];
  const usedMissions = new Set<string>();

  const ordered = [...clashes].sort((a, b) => (a.kind === "overnight_sandwich" ? -1 : b.kind === "overnight_sandwich" ? 1 : 0));
  for (const clash of ordered) {
    const action = proposeActionForClash(clash, workingMissions, workingAssignments, { ...input, now });
    if (!action) continue;
    if (usedMissions.has(action.missionId) && action.kind === "reschedule") continue;
    const beforeCount = clashCount(input, workingMissions, workingAssignments, now);
    const next = applyActionToWorking(action, workingMissions, workingAssignments, input.fleet, input.scenery);
    // A proposal that does not strictly shrink the clash list is dropped.
    // Two units in one pass are applied in order, so the second search sees the first move.
    if (clashCount(input, next.missions, next.assignments, now) >= beforeCount) continue;
    workingMissions = next.missions;
    workingAssignments = next.assignments;
    actions.push(action);
    usedMissions.add(action.missionId);
  }

  const remaining = detectClashes({
    ...input,
    now,
    missions: workingMissions,
    assignments: workingAssignments,
  });
  const remainingIds = remaining.map((c) => c.id);
  const actorName = input.actorName?.trim() || "Dispatcher";
  const actorCrewId = input.actorCrewId?.trim() || "";
  const summary = actions.length
    ? remainingIds.length
      ? `Propose ${actions.length} change${actions.length === 1 ? "" : "s"} for ${clashes.length} clash${clashes.length === 1 ? "" : "es"}; ${remainingIds.length} still need a hand move.`
      : `Propose ${actions.length} change${actions.length === 1 ? "" : "s"} — Accept to write the company board.`
    : `No legal auto-fix for ${clashes.length} clash${clashes.length === 1 ? "" : "es"} — move STD / tail by hand.`;

  return {
    id: `plan-${now.getTime().toString(36)}`,
    kind: DISPATCH_PLAN_KIND,
    createdAtUtc: now.toISOString(),
    createdByCrewId: actorCrewId,
    createdByName: actorName,
    status: "proposed",
    boardDateKey: halifaxDateKey(now),
    clashIds: clashes.map((c) => c.id),
    remainingClashIds: remainingIds,
    actions,
    summary,
    clashNotes: clashes.map((c) => c.message),
  };
}

export type AutoResolveResult = {
  missions: Mission[];
  assignments: TripAssignment[];
  /** The plan that was written, already stamped accepted. Null when nothing was safe to apply. */
  plan: DispatchPlan | null;
  /** Clashes still on the board after the auto-resolve — these need a hand move. */
  remaining: DispatchClash[];
  applied: number;
};

/**
 * AI resolves its own conflicts. Propose against the live board and write the
 * plan only when it strictly reduces red clashes — never a lateral shuffle.
 * The Dispatcher can still hand-move or reject; this only removes the babysitting.
 */
export function autoResolveBoardClashes(input: DispatchBrainInput): AutoResolveResult {
  const now = input.now ?? new Date();
  const before = detectClashes({ ...input, now });
  if (!before.length) {
    return { missions: input.missions, assignments: input.assignments, plan: null, remaining: [], applied: 0 };
  }
  const plan = proposeDispatchPlan({ ...input, now });
  if (!plan || !plan.actions.length) {
    return { missions: input.missions, assignments: input.assignments, plan: null, remaining: before, applied: 0 };
  }
  const applied = applyDispatchPlan(
    { missions: input.missions, assignments: input.assignments, fleet: input.fleet, scenery: input.scenery },
    plan,
  );
  const after = detectClashes({ ...input, now, missions: applied.missions, assignments: applied.assignments });
  if (after.length >= before.length) {
    return { missions: input.missions, assignments: input.assignments, plan: null, remaining: before, applied: 0 };
  }
  return {
    missions: applied.missions,
    assignments: applied.assignments,
    plan: decideDispatchPlan(plan, "accepted", { now, crewId: plan.createdByCrewId, name: plan.createdByName }),
    remaining: after,
    applied: plan.actions.length,
  };
}

export function decideDispatchPlan(
  plan: DispatchPlan,
  status: "accepted" | "rejected",
  input: { crewId?: string; name?: string; now?: Date },
): DispatchPlan {
  const now = input.now ?? new Date();
  return {
    ...plan,
    status,
    decidedAtUtc: now.toISOString(),
    decidedByCrewId: input.crewId ?? plan.createdByCrewId,
    decidedByName: input.name ?? plan.createdByName,
  };
}

export function isDispatchPlanStatus(value: unknown): value is DispatchPlanStatus {
  return value === "proposed" || value === "accepted" || value === "rejected";
}

export function isClashKind(value: unknown): value is ClashKind {
  return (
    value === "tail_overlap" ||
    value === "crew_overlap" ||
    value === "duty_overlap" ||
    value === "impossible_turn" ||
    value === "fdp_overflow" ||
    value === "flight_time_overflow" ||
    value === "type_constraint" ||
    value === "grounded_tail" ||
    value === "overnight_sandwich" ||
    value === "weight"
  );
}

function hydrateAction(raw: Partial<DispatchPlanAction>): DispatchPlanAction | null {
  if (!raw?.id || !raw.missionId || !raw.clashId) return null;
  if (
    raw.kind !== "reschedule" &&
    raw.kind !== "reassign_tail" &&
    raw.kind !== "reassign_crew" &&
    raw.kind !== "cancel_mission"
  ) {
    return null;
  }
  return {
    id: raw.id,
    kind: raw.kind,
    missionId: raw.missionId,
    clashId: raw.clashId,
    from: raw.from ?? {},
    to: raw.to ?? {},
    summary: typeof raw.summary === "string" ? raw.summary : raw.kind,
    cancelMissionIds: Array.isArray(raw.cancelMissionIds) ? raw.cancelMissionIds.filter((id): id is string => typeof id === "string") : undefined,
    cancelReason: isCancelReason(raw.cancelReason) ? raw.cancelReason : undefined,
  };
}

/** Hydrate a shared company plan from snapshot / persist. */
export function hydrateDispatchPlan(raw: unknown): DispatchPlan | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Partial<DispatchPlan>;
  if (!row.id || typeof row.id !== "string") return null;
  const status = isDispatchPlanStatus(row.status) ? row.status : "proposed";
  const actions = Array.isArray(row.actions)
    ? row.actions.map((a) => hydrateAction(a as Partial<DispatchPlanAction>)).filter((a): a is DispatchPlanAction => Boolean(a))
    : [];
  return {
    id: row.id,
    kind: DISPATCH_PLAN_KIND,
    createdAtUtc: typeof row.createdAtUtc === "string" ? row.createdAtUtc : new Date().toISOString(),
    createdByCrewId: typeof row.createdByCrewId === "string" ? row.createdByCrewId : "",
    createdByName: typeof row.createdByName === "string" ? row.createdByName : "Dispatcher",
    status,
    decidedAtUtc: typeof row.decidedAtUtc === "string" ? row.decidedAtUtc : undefined,
    decidedByCrewId: typeof row.decidedByCrewId === "string" ? row.decidedByCrewId : undefined,
    decidedByName: typeof row.decidedByName === "string" ? row.decidedByName : undefined,
    boardDateKey: typeof row.boardDateKey === "string" ? row.boardDateKey : "",
    clashIds: Array.isArray(row.clashIds) ? row.clashIds.filter((id): id is string => typeof id === "string") : [],
    remainingClashIds: Array.isArray(row.remainingClashIds)
      ? row.remainingClashIds.filter((id): id is string => typeof id === "string")
      : [],
    actions,
    summary: typeof row.summary === "string" ? row.summary : "",
    clashNotes: Array.isArray(row.clashNotes) ? row.clashNotes.filter((n): n is string => typeof n === "string") : [],
  };
}
